import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Download, Smartphone, Share2, Info } from "lucide-react";
import { toast } from "sonner";

/**
 * PWAInstallButton
 * - Android / desktop: uses beforeinstallprompt when the browser supports it.
 * - iOS Safari: shows a dialog with Add-to-Home-Screen instructions (no programmatic API).
 * - If already running in standalone mode: renders nothing.
 */

const isIOS = () => /iPhone|iPad|iPod/i.test(navigator.userAgent);
const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)")?.matches ||
  window.navigator.standalone === true;

const PWAInstallButton = ({ variant = "outline", size = "default", className = "" }) => {
  const [deferred, setDeferred] = useState(null);
  const [standalone, setStandalone] = useState(false);
  const [showIosGuide, setShowIosGuide] = useState(false);

  useEffect(() => {
    setStandalone(isStandalone());
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setDeferred(null);
      setStandalone(true);
      toast.success("Vivo has been installed on your device");
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (standalone) {
    return (
      <div className={`text-xs text-muted-foreground flex items-center gap-1.5 ${className}`} data-testid="pwa-installed-note">
        <Smartphone className="w-3.5 h-3.5 text-primary" />
        Running as installed app
      </div>
    );
  }

  const handleClick = async () => {
    if (deferred) {
      try {
        deferred.prompt();
        const { outcome } = await deferred.userChoice;
        if (outcome === "accepted") {
          toast.success("Installing Vivo…");
        } else {
          toast.info("Install dismissed — you can try again any time");
        }
        setDeferred(null);
      } catch {
        toast.error("Install failed");
      }
    } else if (isIOS()) {
      setShowIosGuide(true);
    } else {
      toast.info("To install Vivo, open this site in Chrome or Edge on your phone and tap the menu → Install app.");
    }
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        className={className}
        onClick={handleClick}
        data-testid="pwa-install-btn"
      >
        <Download className="w-4 h-4 mr-2" />
        Save as app
      </Button>
      {showIosGuide && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowIosGuide(false)}
          data-testid="ios-install-guide"
        >
          <div
            className="bg-card border border-white/10 rounded-2xl p-6 max-w-md space-y-3 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-heading text-lg">Add Vivo to your Home Screen</h3>
                <p className="text-xs text-muted-foreground">iPhone &amp; iPad (Safari)</p>
              </div>
            </div>
            <ol className="text-sm space-y-2 list-decimal ml-5 text-muted-foreground">
              <li>Tap the <strong><Share2 className="w-3.5 h-3.5 inline" /> Share</strong> button at the bottom of Safari.</li>
              <li>Scroll and tap <strong>&ldquo;Add to Home Screen&rdquo;</strong>.</li>
              <li>Tap <strong>Add</strong>. Vivo now lives on your home screen like any other app.</li>
            </ol>
            <div className="flex items-start gap-2 text-[11px] text-muted-foreground p-2 rounded-lg bg-white/[0.03] border border-white/5">
              <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>If you&apos;re using a browser other than Safari, open this site in Safari first — iOS only allows Safari to install web apps.</span>
            </div>
            <Button className="w-full" onClick={() => setShowIosGuide(false)}>Got it</Button>
          </div>
        </div>
      )}
    </>
  );
};

export default PWAInstallButton;
